/**
 * Canonical status vocabularies.
 *
 * These values are persisted, so renaming one requires a database migration.
 */

export const USER_STATUSES = ["active", "invited", "suspended", "closed"] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

export const ORGANISATION_STATUSES = ["active", "suspended", "closed"] as const;
export type OrganisationStatus = (typeof ORGANISATION_STATUSES)[number];

export const ORGANISATION_ROLES = ["owner", "admin", "user", "billing", "support_viewer"] as const;
export type OrganisationRole = (typeof ORGANISATION_ROLES)[number];

export const MEMBERSHIP_STATUSES = ["active", "invited", "revoked"] as const;
export type MembershipStatus = (typeof MEMBERSHIP_STATUSES)[number];

export const PLAN_STATUSES = ["draft", "active", "retired"] as const;
export type PlanStatus = (typeof PLAN_STATUSES)[number];

export const BILLING_PERIODS = ["monthly", "annual"] as const;
export type BillingPeriod = (typeof BILLING_PERIODS)[number];

/**
 * Bafe is sold by storage. Counting policies exist so an operator can apply a
 * safety ceiling to a pathological account, not so we can bill by seat.
 */
export const COUNT_POLICIES = ["unlimited_fair_use", "capped"] as const;
export type CountPolicy = (typeof COUNT_POLICIES)[number];

export const SUPPORT_LEVELS = ["standard", "priority", "enterprise"] as const;
export type SupportLevel = (typeof SUPPORT_LEVELS)[number];

export const SUBSCRIPTION_STATUSES = [
	"trialing",
	"active",
	"past_due",
	"grace_period",
	"restricted",
	"suspended",
	"cancelled",
	"expired",
] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

export const DOMAIN_STATUSES = [
	"pending",
	"verifying",
	"connected",
	"degraded",
	"suspended",
	"removing",
	"removed",
] as const;
export type DomainStatus = (typeof DOMAIN_STATUSES)[number];

export const PROVISIONING_STATES = [
	"draft",
	"ownership_pending",
	"ownership_verified",
	"mail_engine_provisioning",
	"dns_pending",
	"mail_ready",
	"provisioning_failed",
	"dns_degraded",
	"suspended",
	"removing",
	"removed",
] as const;
export type ProvisioningState = (typeof PROVISIONING_STATES)[number];

export const DNS_RECORD_TYPES = ["A", "AAAA", "MX", "TXT", "CNAME", "SRV", "ALIAS"] as const;
export type DnsRecordType = (typeof DNS_RECORD_TYPES)[number];

/**
 * Hub and Webmail are custom hostnames, not apex capabilities.
 * Email and sites may share one apex hostname.
 */
export const PLATFORM_APPS = ["hub", "webmail"] as const;
export type PlatformApp = (typeof PLATFORM_APPS)[number];

export const DNS_RECORD_PURPOSES = [
	"ownership",
	"mx",
	"spf",
	"dkim",
	"dmarc",
	"autodiscover",
	"autoconfig",
	"mta_sts",
	"tls_rpt",
	"site",
	"cname",
	"platform",
] as const;
export type DnsRecordPurpose = (typeof DNS_RECORD_PURPOSES)[number];

export const DNS_RECORD_STATUSES = [
	"pending",
	"detected",
	"incorrect",
	"conflict",
	"optional",
] as const;
export type DnsRecordStatus = (typeof DNS_RECORD_STATUSES)[number];

export const MAILBOX_STATUSES = [
	"provisioning",
	"active",
	"suspended",
	"over_quota",
	"scheduled_for_deletion",
	"deleting",
	"deleted",
] as const;
export type MailboxStatus = (typeof MAILBOX_STATUSES)[number];

export const MAILBOX_PROVISIONING_STATES = [
	"pending",
	"provisioning",
	"ready",
	"failed",
	"removing",
	"removed",
] as const;
export type MailboxProvisioningState = (typeof MAILBOX_PROVISIONING_STATES)[number];

export const CREDENTIAL_ACTIONS = [
	"password_set",
	"password_reset",
	"app_password_created",
	"app_password_revoked",
] as const;
export type CredentialAction = (typeof CREDENTIAL_ACTIONS)[number];

export const ALIAS_DESTINATION_TYPES = ["mailbox", "external", "group"] as const;
export type AliasDestinationType = (typeof ALIAS_DESTINATION_TYPES)[number];

export const ALIAS_STATUSES = ["provisioning", "active", "suspended", "removing", "removed"] as const;
export type AliasStatus = (typeof ALIAS_STATUSES)[number];

export const LIST_MEMBER_TYPES = ["mailbox", "external"] as const;
export type ListMemberType = (typeof LIST_MEMBER_TYPES)[number];

export const MIGRATION_STATUSES = [
	"draft",
	"checking",
	"ready",
	"queued",
	"migrating",
	"retrying",
	"completed",
	"completed_with_errors",
	"failed",
	"cancelled",
] as const;
export type MigrationStatus = (typeof MIGRATION_STATUSES)[number];

export const SOURCE_SECURITY = ["ssl", "starttls", "none"] as const;
export type SourceSecurity = (typeof SOURCE_SECURITY)[number];

export const JOB_STATUSES = ["queued", "running", "succeeded", "failed", "cancelled"] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

export const JOB_RESOURCE_TYPES = ["domain", "mailbox", "alias", "list", "migration", "subscription"] as const;
export type JobResourceType = (typeof JOB_RESOURCE_TYPES)[number];

export const JOB_OPERATIONS = [
	"create",
	"update",
	"delete",
	"suspend",
	"resume",
	"reset_password",
	"check_dns",
	"sync_usage",
	"reconcile",
] as const;
export type JobOperation = (typeof JOB_OPERATIONS)[number];

export const INVOICE_STATUSES = ["draft", "open", "paid", "void", "uncollectible"] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const ACTOR_TYPES = ["user", "operator", "system", "provider"] as const;
export type ActorType = (typeof ACTOR_TYPES)[number];

export const SUPPORT_CATEGORIES = [
	"domain_setup",
	"sending",
	"receiving",
	"mailbox",
	"migration",
	"billing",
	"security",
	"other",
] as const;
export type SupportCategory = (typeof SUPPORT_CATEGORIES)[number];

export const SUPPORT_PRIORITIES = ["low", "normal", "high", "urgent"] as const;
export type SupportPriority = (typeof SUPPORT_PRIORITIES)[number];

export const SUPPORT_STATUSES = ["open", "pending_customer", "pending_bafe", "resolved", "closed"] as const;
export type SupportStatus = (typeof SUPPORT_STATUSES)[number];

export const ABUSE_EVENT_TYPES = [
	"send_volume_spike",
	"recipient_diversity_spike",
	"auth_failure_burst",
	"hard_bounce_rate",
	"spam_rejection_rate",
	"suspected_compromise",
	"rapid_alias_creation",
	"rapid_mailbox_creation",
	"manual_report",
] as const;
export type AbuseEventType = (typeof ABUSE_EVENT_TYPES)[number];

export const ABUSE_SEVERITIES = ["info", "warning", "critical"] as const;
export type AbuseSeverity = (typeof ABUSE_SEVERITIES)[number];

export const ABUSE_STATUSES = ["open", "investigating", "actioned", "dismissed", "resolved"] as const;
export type AbuseStatus = (typeof ABUSE_STATUSES)[number];

export const DOMAIN_HEALTH = ["healthy", "attention", "degraded", "suspended"] as const;
export type DomainHealth = (typeof DOMAIN_HEALTH)[number];

export const CHECK_STATES = ["pass", "warn", "fail", "unknown"] as const;
export type CheckState = (typeof CHECK_STATES)[number];
